import React, { useEffect, useRef, useState } from "react";
import { Button } from "react-bootstrap";
import { useNavigate } from "react-router";
import { LoadingComponent } from "../components/LoadingComponent";
import { useProfile } from "../components/ProfileContext";
import { RentalStatusDisplay } from "../components/RentalStatusDisplay";
import { Timestamp } from "../components/Timestamp";
import { Api } from "../util/Api";
import { afterRenderDelay, arrayToCSV, downloadBlobToNewWindow, downloadPdfDocumentToNewWindow, formatFilename, pageReadyCondition, setNavAdvisoryLockout, stringToDate, useIsVault } from "../util/utils";
import { inspectionInterval } from "../dependencies";
import { Inspection } from "../models/Inspection";
import { SpinnerButton } from "../components/SpinnerButton";
import { getDB } from "../util/db";
import { FiletypeCsv, FiletypePdf, Search } from "react-bootstrap-icons";
import { GroupingDisplay } from "../components/GroupingDisplay";
import { SharingStatus } from "../components/SharingStatus";
import { ComponentType } from "../models/ComponentType";

export function AssetListingPage(props) {
	setNavAdvisoryLockout(false);

	let [profile] = useProfile();
	let [searchResults, setSearchResults] = useState(null);
	let [isLoading, setIsLoading] = useState(false);
	let isVault = useIsVault();
	let [showSearchBar, setShowSearchBar] = useState(isVault);
	let [isPdfGenerating, setIsPdfGenerating] = useState(false);
	let [isCsvGenerating, setIsCsvGenerating] = useState(false);
	let [componentTypes, setComponentTypes] = useState(null);
	let [jobsById, setJobsById] = useState(null);
	let navigate = useNavigate();
	let dataGroupingsRef = useRef();

	// Default to job mode if (a) there is a job, and (b) we are not doing rentals
	let jobMode = !!profile.job;

	let online = navigator.onLine;
	let pageCondition = pageReadyCondition((!online) || searchResults != null);

	let expirationCutoffMillis = Date.now() - inspectionInterval;

	/* *****  Basic Grouping Setup ***** */
	let showAsset = (asset) => () => {
		navigate(`/assets/${encodeURIComponent(asset.assetNumber)}`);
	};

	let dFormatter = profile.dateFormatter;

	let getAssetTableRow = (grp, asset) => {
		return <tr key={asset.inspection.id}>
			<td><Button onClick={showAsset(asset)}>Show</Button></td>
			<td>{asset.assetNumber} <RentalStatusDisplay inspection={asset.inspection} /> <SharingStatus ownerUid={asset.inspection.userIdentifier} profile={profile} childAutoAccess={false} /></td>
			<td>{asset.serialNumbers}</td>
			<td>{asset.modelNumbers}</td>
			<td>{asset.componentTypeNames}</td>
			<td><Timestamp dateString={asset.inspection.completedAt} expirationCutoffMillis={expirationCutoffMillis} formatter={dFormatter} /></td>
			<td>{asset.inspection.result}</td>
			<td>{asset.inspection.disposition}</td>
		</tr>;
	};

	let getAssetTableHeader = (grp) => {
		return <tr>
			<th></th>
			<th>Asset</th>
			<th>Serial</th>
			<th>Model</th>
			<th>Component</th>
			<th>Last&nbsp;Insp</th>
			<th>Result</th>
			<th>Disposition</th>
		</tr>;
	};

	let groupingOptions = [
		{
			name: "model",
			keyPath: ["modelNumbers"],
			label: "Model #"
		},
		{
			name: "component",
			keyPath: ["componentTypeNames"],
			label: "Component"
		},
		{
			name: "result",
			keyPath: ["inspection", "result"],
			label: "Result"
		},
		{
			name: "disposition",
			keyPath: ["inspection", "disposition"],
			label: "Disposition"
		},
		{
			name: "location",
			keyPath: ["inspection", "location"],
			label: "Location"
		},
		{
			name: "company",
			keyPath: ["inspection", "companyName"],
			label: "Company"
		}
	];
	if(profile.rentalMode) {
		groupingOptions.push({
			name: "rental",
			keyPath: ["inspection", "rentalDisposition"],
			label: "Check In/Out"
		});
	}
	if(profile.job == null) {
		groupingOptions.push({
			name: "job",
			keyPath: (asset) => {
				let jobId = asset.inspection.jobId;
				if(jobId == null) {
					return null;
				}
				let job = jobsById[jobId];
				if(job == null) {
					return null;
				}
				return job.name;
			},
			label: "Job"
		})
	}

	let sortingOptions = [
		{
			name: "date",
			keyPath: ["inspection", "completedAt"],
			label: "Inspection Date"
		},
		{
			name: "asset",
			keyPath: ["assetNumber"],
			label: "Asset #"
		},
		{
			name: "serial",
			keyPath: ["serialNumbers"],
			label: "Serial #"
		},
		{
			name: "model",
			keyPath: ["modelNumbers"],
			label: "Model #"
		},
		{
			name: "component",
			keyPath: ["componentTypeNames"],
			label: "Component(s)"
		},
		{
			name: "result",
			keyPath: ["inspection", "result"],
			label: "Result"
		},
		{
			name: "disposition",
			keyPath: ["inspection", "disposition"],
			label: "Disposition",
		}
	];

	let filterKeyPaths = [
		["assetNumber"],
		["modelNumbers"],
		["serialNumbers"],
		["inspection", "companyName"],
		["inspection", "location"],
		["componentTypeNames"]
	];



	/* ****** Basic asset filtering ********** */

	// Will be used to filter the jobs based on the search query
	let filterKey = function (inspection) {
		let result = true;
		for(const [key, val] of Object.entries(search)){
			const item = val.getItem(inspection);
			if (val.type === "daterange"){
				const itemTime = new Date(item);
				if(val.start){
					const start = new Date(val.start);
					start.setHours(0);
					result = result && start < itemTime;
				}
				if(val.end){
					const end = new Date(val.end);
					end.setUTCHours(23);
					end.setUTCMinutes(59);
					end.setUTCSeconds(59);

					result = result && itemTime < end;
				}
			} else if (val.type === "string_submatch"){
				result = result && item.toLowerCase().includes(val.value.toLowerCase());
			}

			if(!result){
				return result;
			}
		}
		return result;
	}

	// sortDir of 1 means you should reverse the sort
	const [search, setSearch] = useState({
		// "asset": {
		// 	type: "string_submatch",
		// 	value: "",
		// 	label: "Asset",
		// 	sortDir: -1,
		// 	getItem: (inspection) => { return inspection.assetNumber }
		// },
		"lastInsp": {
			type: "daterange",
			start: "",
			end: "",
			label: "Last Insp Date",
			sortDir: -1,
			getItem: (inspection) => { return inspection.inspection.completedAt }
		}
	});

	const onDateChange = function(start, end){
		const x = {
			...search,
			"lastInsp": {
				...search["lastInsp"],
				start: start,
				end: end
			}
		}
		setSearch(x);
	}

	/* ***** Data Loading ***** */
	let loadPageData = async (myJobMode) => { // Job Mode needs to be passed because it might not be rendered yet
		setIsLoading(true);
		try {
			if(componentTypes == null) {
				let db = await getDB();
				let allCT = {};
				let tmpComponentTypes = await db.getAll("ComponentTypes");
				tmpComponentTypes.forEach(ct => {
					allCT[ct.id] = ct;
				});
				componentTypes = Object.values(allCT);
				setComponentTypes(componentTypes);
			}
			if(jobsById == null) {
				let db = await getDB();
				let jobs = await db.getAll("Jobs");
				let tmp = {};
				jobs.forEach(job => {
					tmp[job.id] = job;
				});
				setJobsById(tmp);
			}

			let myJobId = (myJobMode && profile.job != null) ? profile.job.id : null;

			// Used for combining results
			let allResults = {};

			// Get remote results
			let searchParams = {};
			if(!!myJobId) {
				searchParams["jobId"] = myJobId;
			}
			let assets = await Api.forProfile(profile).getAllAssets(searchParams);
			for(const asset of assets) {
				allResults[asset.assetNumber] = asset;
			}

			// Merge in Local results
			const ctypeLookup = await ComponentType.getComponentTypeLookup();
			let localInspections = await Inspection.loadAllFromLocalByJobId(myJobId);
			for(let insp of localInspections) {
 				let localAsset = {
					assetNumber: insp.assetNumber ?? insp.componentAssetNumber,
					assetDescription: insp.assetDescription,
					inspection: insp,
					survey: insp.survey
				}

				// Merge in components

				// NOTE - need to sort out whether .components or .inspectionComponents is preferred
				if(insp.components != null) {
					// Latest method - we will get an array of components
					localAsset.components = insp.components;
				} else {
					// Previous method - it is a hash from inspectionComponents wiht a key of the component type id

					// Grab component list
					if(insp.inspectionComponents != null) {
						let components = [];
						for(const ctypeid in insp.inspectionComponents) {
							components.push(insp.inspectionComponents[ctypeid]);
						}
						localAsset.components = components;
						// NOTE - may want to sort in the future based on position (see EI-261)
					}
				}

				// NOTE - may want to check completionDate to make sure the inspection on the device is newer
				allResults[localAsset.assetNumber] = localAsset;
			}

			// Get combined results
			const allAssets = Object.values(allResults);

			// Flatten Model/Serial/Ctype
			for(const asset of allAssets) {
				Inspection.flattenComponentsFromHeader(asset, ctypeLookup);
			}

			setSearchResults(allAssets);
		} catch(err) {
			console.log("Error loading assets");
			console.log(err);
			alert(`Unable to load remote assets: ${err}`);
		}
	};

	useEffect(async () => {
		if(online && !isLoading) {
			await loadPageData(jobMode);
		}
	});

	/* ***** Page Controller Actions ***** */
	let downloadPdfAction = () => {
		setIsPdfGenerating(true);
		afterRenderDelay(async () => {
			try {
				let inspectionHeaders = [];
				dataGroupingsRef.current.forEach(grouping => {
					if(grouping != null) {
						grouping.data.forEach(asset => {
							inspectionHeaders.push(asset.inspection);
						})
					}
				});
				let pdf = await Inspection.generatePdfDocumentFromInspectionHeaders(profile, inspectionHeaders);
				await downloadPdfDocumentToNewWindow(pdf, formatFilename("pdf", profile.dateFormatter(new Date()), profile.job?.name, "assets"));
			} catch(err) {
				alert(`Error generating PDF: ${err}`);
			}
			setIsPdfGenerating(false);
		});
	};

	let downloadCsvAction = () => {
		setIsCsvGenerating(true);

		afterRenderDelay(async () => {
			let data = [];
			dataGroupingsRef.current.forEach(grouping => {
				if(grouping != null) {
					data.push([grouping.key])
					data.push(["Asset #", "Model #", "Serial #", "Result", "Disposition", "Timestamp", "Company", "Location", "Rental Disposition"]);
					grouping.data.forEach(asset => {
						data.push([asset.assetNumber, asset.modelNumbers, asset.serialNumbers, asset.inspection.result, asset.inspection.disposition, profile.dateFormatter(stringToDate(asset.inspection.completedAt)), asset.inspection.companyName, asset.inspection.location, asset.inspection.rentalDisposition]);
					})
				}
			});

			let csvData = arrayToCSV(data);
			let csvBlob = new Blob([csvData], {type:"text/csv"});
			downloadBlobToNewWindow(csvBlob, formatFilename("csv", profile.dateFormatter(new Date()), profile.job?.name, "assets"));
			setIsCsvGenerating(false);
		});
	};

	const filteredResults = searchResults?.filter(filterKey);

	return <div className="assetListingPageContainer">
		<h1>
			Assets
			<SpinnerButton spinning={isPdfGenerating} className="mt-0 float-end filetypeButton" onClick={downloadPdfAction} spinnerStyle={{color: "black"}}><FiletypePdf className="filetypeIcon" /></SpinnerButton>
			<SpinnerButton spinning={isCsvGenerating} className="mt-0 float-end filetypeButton" onClick={downloadCsvAction} spinnerStyle={{color: "black"}}><FiletypeCsv className="filetypeIcon" /></SpinnerButton>
			<Button className="mt-1 ms-1 float-end" variant="info" onClick={() => {setShowSearchBar(!showSearchBar)}}><Search /> Tools</Button>
		</h1>
		<div className="clearfix"></div>
		{pageCondition != "ready" && <LoadingComponent />}
		{!online && <p>Page requires Internet connectivity.</p>}
		{pageCondition == "ready" && <GroupingDisplay data={filteredResults} dataGroupingsRef={dataGroupingsRef} sortingOptions={sortingOptions} groupingOptions={groupingOptions} showSearchTools={showSearchBar} headerBuilder={getAssetTableHeader} rowBuilder={getAssetTableRow} filterKeyPaths={filterKeyPaths} dateRangeOptions={search["lastInsp"]} onDateChange={onDateChange}/>}
	</div>
}